import { z } from 'zod';
import { TenantSettingsSchema } from '../config/tenant-settings.ts';
import {
  ApiKeyIdSchema,
  ConsoleAccountIdSchema,
  ConsoleInviteIdSchema,
  McpServerNameSchema,
  TenantIdSchema,
} from '../ids.ts';
import { TOOL_DESCRIPTION_MAX_CHARS, ToolSourceKindSchema } from '../tools/sources.ts';

export const ConsoleRoleSchema = z.enum(['admin', 'editor', 'viewer']);
export type ConsoleRole = z.infer<typeof ConsoleRoleSchema>;

const ROLE_RANK: Readonly<Record<ConsoleRole, number>> = { viewer: 0, editor: 1, admin: 2 };

export function hasConsoleRole(role: ConsoleRole, needed: ConsoleRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[needed];
}

export const SignInProviderSchema = z.enum(['google', 'microsoft', 'dev']);
export type SignInProvider = z.infer<typeof SignInProviderSchema>;

export const ConsoleSignInOptionsSchema = z.object({
  providers: z.array(z.object({ id: SignInProviderSchema, label: z.string() })),
});
export type ConsoleSignInOptions = z.infer<typeof ConsoleSignInOptionsSchema>;

export const ConsoleDevSignInRequestSchema = z.object({
  email: z.email().max(254),
  name: z.string().trim().min(1).max(200).optional(),
  plato_staff: z.boolean().default(true),
});
export type ConsoleDevSignInRequest = z.input<typeof ConsoleDevSignInRequestSchema>;

export const ConsoleAccountSchema = z.object({
  id: ConsoleAccountIdSchema,
  email: z.string().nullable(),
  name: z.string().nullable(),
  plato_staff: z.boolean(),
});
export type ConsoleAccount = z.infer<typeof ConsoleAccountSchema>;

export const ConsoleTenantSchema = z.object({
  id: TenantIdSchema,
  name: z.string(),
  key_mode: z.enum(['test', 'live']),
  role: ConsoleRoleSchema,
  via: z.enum(['member', 'staff']),
});
export type ConsoleTenant = z.infer<typeof ConsoleTenantSchema>;

export const ConsoleMeSchema = z.object({
  account: ConsoleAccountSchema,
  tenants: z.array(ConsoleTenantSchema),
});
export type ConsoleMe = z.infer<typeof ConsoleMeSchema>;

export const ConsoleMemberSchema = z.object({
  account_id: ConsoleAccountIdSchema,
  email: z.string().nullable(),
  name: z.string().nullable(),
  role: ConsoleRoleSchema,
  joined_at: z.string(),
});
export type ConsoleMember = z.infer<typeof ConsoleMemberSchema>;

export const ConsoleInviteSchema = z.object({
  id: ConsoleInviteIdSchema,
  email: z.string(),
  role: ConsoleRoleSchema,
  invited_by: ConsoleAccountIdSchema.nullable(),
  expires_at: z.string(),
  created_at: z.string(),
});
export type ConsoleInvite = z.infer<typeof ConsoleInviteSchema>;

export const ConsoleTeamSchema = z.object({
  members: z.array(ConsoleMemberSchema),
  invites: z.array(ConsoleInviteSchema),
});
export type ConsoleTeam = z.infer<typeof ConsoleTeamSchema>;

export const ConsoleInviteCreateRequestSchema = z.object({
  email: z.email().max(254),
  role: ConsoleRoleSchema,
});
export type ConsoleInviteCreateRequest = z.infer<typeof ConsoleInviteCreateRequestSchema>;

export const ConsoleInviteCreatedSchema = z.object({
  invite: ConsoleInviteSchema,
  link: z.string(),
});
export type ConsoleInviteCreated = z.infer<typeof ConsoleInviteCreatedSchema>;

export const ConsoleMemberUpdateRequestSchema = z.object({ role: ConsoleRoleSchema });
export type ConsoleMemberUpdateRequest = z.infer<typeof ConsoleMemberUpdateRequestSchema>;

export const ConsoleInviteAcceptRequestSchema = z.object({ token: z.string().min(20).max(200) });
export type ConsoleInviteAcceptRequest = z.infer<typeof ConsoleInviteAcceptRequestSchema>;

export const ConsoleInviteAcceptedSchema = z.object({ tenant: ConsoleTenantSchema });
export type ConsoleInviteAccepted = z.infer<typeof ConsoleInviteAcceptedSchema>;

export const ConsoleSettingsSchema = z.object({ settings: TenantSettingsSchema });
export type ConsoleSettings = z.infer<typeof ConsoleSettingsSchema>;

export const ConsoleSettingsUpdateRequestSchema = z.object({
  settings: z.record(z.string(), z.unknown()),
});
export type ConsoleSettingsUpdateRequest = z.infer<typeof ConsoleSettingsUpdateRequestSchema>;

export const ConsoleMcpServerSchema = z.object({
  name: McpServerNameSchema,
  title: z.string().nullable(),
  url: z.string(),
  enabled: z.boolean(),
  credential: z.enum(['session_token', 'oauth_refresh', 'user_sign_in', 'user_api_key']),
  token_endpoint: z.string().nullable(),
  client_id: z.string().nullable(),
});
export type ConsoleMcpServer = z.infer<typeof ConsoleMcpServerSchema>;

export const ConsoleApiKeySchema = z.object({
  id: ApiKeyIdSchema,
  kind: z.enum(['secret', 'publishable']),
  key_mode: z.enum(['test', 'live']),
  created_at: z.string(),
  revoked_at: z.string().nullable(),
  publishable_key: z.string().nullable(),
});
export type ConsoleApiKey = z.infer<typeof ConsoleApiKeySchema>;

export const ConsoleIntegrationsSchema = z.object({
  api_origin: z.string(),
  embed_origin: z.string(),
  allowed_origins: z.array(z.string()),
  mcp_servers: z.array(ConsoleMcpServerSchema),
  api_keys: z.array(ConsoleApiKeySchema),
});
export type ConsoleIntegrations = z.infer<typeof ConsoleIntegrationsSchema>;

export const ConsoleOriginsUpdateRequestSchema = z.object({
  origins: z.array(z.string().max(255)).max(50),
});
export type ConsoleOriginsUpdateRequest = z.infer<typeof ConsoleOriginsUpdateRequestSchema>;

export const ConsoleMcpServerUpdateRequestSchema = z
  .object({ enabled: z.boolean().optional(), url: z.string().max(2048).optional() })
  .refine((body) => body.enabled !== undefined || body.url !== undefined, {
    message: 'give enabled, url or both',
  });
export type ConsoleMcpServerUpdateRequest = z.infer<typeof ConsoleMcpServerUpdateRequestSchema>;

export const ConsoleMcpServerCreateRequestSchema = z.object({
  name: McpServerNameSchema,
  title: z.string().trim().min(1).max(64),
  url: z.string().max(2048),
  connect_with: z.enum(['sign_in', 'api_key']),
  client_id: z.string().trim().min(1).max(256).optional(),
  client_secret: z.string().min(1).max(4096).optional(),
});
export type ConsoleMcpServerCreateRequest = z.infer<typeof ConsoleMcpServerCreateRequestSchema>;

export const ConsoleApiKeyCreateRequestSchema = z.object({
  kind: z.enum(['secret', 'publishable']),
});
export type ConsoleApiKeyCreateRequest = z.infer<typeof ConsoleApiKeyCreateRequestSchema>;

export const ConsoleApiKeyCreatedSchema = z.object({
  key: ConsoleApiKeySchema,
  value: z.string(),
});
export type ConsoleApiKeyCreated = z.infer<typeof ConsoleApiKeyCreatedSchema>;

export const ConsoleHistoryEntrySchema = z.object({
  id: z.string(),
  at: z.string(),
  actor: z.object({
    kind: z.enum(['console', 'operator', 'system']),
    id: z.string(),
    label: z.string(),
  }),
  action: z.string(),
  details: z.record(z.string(), z.unknown()),
});
export type ConsoleHistoryEntry = z.infer<typeof ConsoleHistoryEntrySchema>;

export const ConsoleHistorySchema = z.object({ data: z.array(ConsoleHistoryEntrySchema) });
export type ConsoleHistory = z.infer<typeof ConsoleHistorySchema>;

export const ConsoleToolSchema = z.object({
  source: McpServerNameSchema,
  kind: ToolSourceKindSchema,
  source_enabled: z.boolean(),
  name: z.string(),
  title: z.string().nullable(),
  source_description: z.string().nullable(),
  description: z.string().nullable(),
  description_replaced: z.boolean(),
  enabled: z.boolean(),
  last_seen_at: z.string().nullable(),
});
export type ConsoleTool = z.infer<typeof ConsoleToolSchema>;

export const ConsoleToolsSchema = z.object({ data: z.array(ConsoleToolSchema) });
export type ConsoleTools = z.infer<typeof ConsoleToolsSchema>;

export const ConsoleToolUpdateRequestSchema = z
  .object({
    enabled: z.boolean().optional(),
    description: z.string().max(TOOL_DESCRIPTION_MAX_CHARS).nullable().optional(),
  })
  .refine((body) => body.enabled !== undefined || body.description !== undefined, {
    message: 'give enabled, description or both',
  });
export type ConsoleToolUpdateRequest = z.infer<typeof ConsoleToolUpdateRequestSchema>;
